import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { openExperience, openFlow } from 'next/lib/builder';
import { asRequest } from 'next/entities/requests';
import { Shape as PinShape } from 'next/entities/experiences';
import { Shape as FlowShape } from 'next/entities/flows';

import { Button } from 'next/components/ui';
import { OpenInBuilderManager } from 'next/components/OpenInBuilder';
import CreateExperienceModal from './CreateExperienceModal';
import { FLOW_TYPE, MODAL_STATE, PIN_TYPE } from './constants';
import { singularExperienceType } from './lib';

const CreateExperienceModalManager = ({
  onOpen,
  onSubmit,
  status = {},
  type,
  ...unhandledProps
}) => {
  const [showModal, setShowModal] = useState(null);
  const [inFlight, setInFlight] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [id, setId] = useState(null);
  const { loading, error } = status;

  // Clear modal and set inFlight when modal is opened
  useEffect(() => {
    if (showModal === MODAL_STATE.create) {
      setInFlight(false);
      onOpen?.();
    }
  }, [showModal, onOpen]);

  // Close modal if inFlight
  useEffect(() => {
    if (inFlight && loading == null) {
      setShowModal(null);
    }

    // NOTE: inFlight is not part of the dependency check.
    //       If so, it will cause the modal to close before it must.
    //       This is due to using a placeholder ID when creating new items.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, setShowModal]);

  // Open Builder after successful submission
  useEffect(() => {
    if (inFlight && !error) {
      setShowModal(MODAL_STATE.builder);
      setInFlight(false);
    }
  }, [error, inFlight, setShowModal, setInFlight]);

  const handleSubmit = async (event, values) => {
    event.preventDefault();
    try {
      setInFlight(true);

      const { previewUrl: createdPreviewUrl, id: createdId } = await onSubmit(
        values
      );

      setPreviewUrl(createdPreviewUrl);
      setId(createdId);
    } catch {
      // TODO: handle error
    }
  };

  const handleClose = () => void setShowModal(null);

  return (
    <>
      <Button onClick={() => setShowModal(MODAL_STATE.create)}>
        Create a {singularExperienceType(type)}
      </Button>
      <CreateExperienceModal
        error={error}
        onClose={handleClose}
        onSubmit={handleSubmit}
        status={status}
        type={type}
        visible={showModal === MODAL_STATE.create}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...unhandledProps}
      />
      <OpenInBuilderManager
        onClose={() => setShowModal(null)}
        /**
         * TODO: Once the experience is created, use promisaga chaining to
         *       progress to the next modal AND use the ID from creation
         *       response to navigate directly to the experience in the builder
         *       via `next/lib/builder#openExperience`.
         */
        onNavigate={useCallback(() => {
          if (type === PIN_TYPE) {
            openExperience({ experience: id, url: previewUrl });
          } else {
            openFlow({ flow: id, url: previewUrl });
          }
        }, [type, id, previewUrl])}
        type={type}
        visible={showModal === MODAL_STATE.builder}
      />
    </>
  );
};

CreateExperienceModalManager.propTypes = {
  onOpen: PropTypes.func,
  onSubmit: PropTypes.func,
  status: asRequest(PropTypes.objectOf(PropTypes.oneOf([PinShape, FlowShape]))),
  type: PropTypes.oneOf([FLOW_TYPE, PIN_TYPE]),
};

export default CreateExperienceModalManager;
